import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';

interface TestResult {
  status: number;
  body: any;
  timing: { totalMs: number };
  error?: string;
}

export function JWTTester() {
  const { t } = useTranslation();
  const { selectedTenant } = useAuthStore();
  const [customJwt, setCustomJwt] = useState('');
  const [testResults, setTestResults] = useState<{
    withJwt?: TestResult;
    withoutJwt?: TestResult;
  }>({});
  const [isLoading, setIsLoading] = useState(false);

  const testEndpoint = async (jwt?: string): Promise<TestResult> => {
    const startTime = Date.now();
    try {
      const headers: Record<string, string> = {
        'x-tenant-id': selectedTenant || 'dev',
        'Content-Type': 'application/json',
      };
      
      if (jwt) {
        headers['Authorization'] = `Bearer ${jwt}`;
      }

      const response = await fetch('/api/telemetry/health', {
        method: 'GET',
        headers,
      });

      const body = await response.json();
      const timing = { totalMs: Date.now() - startTime };

      return {
        status: response.status,
        body,
        timing,
      };
    } catch (error: any) {
      return {
        status: 0,
        body: null,
        timing: { totalMs: Date.now() - startTime },
        error: error.message || 'Network error',
      };
    }
  };

  const runJwtTest = async () => {
    setIsLoading(true);
    setTestResults({});

    try {
      // Test without JWT (should return 401)
      const withoutJwt = await testEndpoint();
      setTestResults(prev => ({ ...prev, withoutJwt }));

      // Wait a moment for visual effect
      await new Promise(resolve => setTimeout(resolve, 500));

      // Test with JWT (should return 200)
      const withJwt = await testEndpoint(customJwt);
      setTestResults(prev => ({ ...prev, withJwt }));
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'bg-green-100 text-green-800 border-green-200';
    if (status >= 400 && status < 500) return 'bg-red-100 text-red-800 border-red-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        {t('apis.jwtTester.title', 'JWT Authentication Test')}
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        {t('apis.jwtTester.description', 'Test JWT authentication by calling /api/telemetry/health with and without a token')}
      </p>

      {/* JWT Input */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('apis.jwtTester.jwtToken', 'JWT Token')}
        </label>
        <textarea
          value={customJwt}
          onChange={(e) => setCustomJwt(e.target.value)}
          placeholder="Paste your JWT token here..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm font-mono"
          rows={3}
        />
      </div>

      {/* Test Button */}
      <button
        onClick={runJwtTest}
        disabled={isLoading || !customJwt.trim()}
        className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 mb-6"
      >
        {isLoading 
          ? t('apis.jwtTester.testing', 'Testing...') 
          : t('apis.jwtTester.runTest', 'Run JWT Test (401 → 200)')
        }
      </button>

      {/* Results */}
      <div className="space-y-4">
        {/* Without JWT Result */}
        {testResults.withoutJwt && (
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">
                {t('apis.jwtTester.withoutJwt', 'Without JWT')}
              </h4>
              <div className="flex items-center space-x-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(testResults.withoutJwt.status)}`}>
                  {testResults.withoutJwt.status}
                </span>
                <span className="text-sm text-gray-600">
                  {testResults.withoutJwt.timing.totalMs}ms
                </span>
              </div>
            </div>
            <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto max-h-32">
              {testResults.withoutJwt.error || JSON.stringify(testResults.withoutJwt.body, null, 2)}
            </pre>
          </div>
        )}

        {/* With JWT Result */}
        {testResults.withJwt && (
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">
                {t('apis.jwtTester.withJwt', 'With JWT')}
              </h4>
              <div className="flex items-center space-x-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(testResults.withJwt.status)}`}>
                  {testResults.withJwt.status}
                </span>
                <span className="text-sm text-gray-600">
                  {testResults.withJwt.timing.totalMs}ms
                </span>
              </div>
            </div>
            <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto max-h-32">
              {testResults.withJwt.error || JSON.stringify(testResults.withJwt.body, null, 2)}
            </pre>
          </div>
        )}

        {/* Success Message */}
        {testResults.withoutJwt && testResults.withJwt && 
         testResults.withoutJwt.status === 401 && testResults.withJwt.status === 200 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-green-600 text-xl mr-3">✅</div>
              <div>
                <h4 className="font-medium text-green-900">
                  {t('apis.jwtTester.success', 'JWT Authentication Working')}
                </h4>
                <p className="text-sm text-green-700">
                  {t('apis.jwtTester.successDescription', 'Successfully demonstrated 401 → 200 authentication flow')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
